// Batch 29 · M69 — saved work always opens. The API answers a failing screen read with the screen's last-good copy (headers
// X-AZEX-Fallback / X-AZEX-Saved-At / X-AZEX-Saved-By) or, when the screen was never saved, 503 SCREEN_NO_SAVED_COPY.
// The API layer (lib/api.js) reports both here; components/SavedCopyFrame.jsx shows the banner, turns editing off, and — when
// the screen itself fails to render — shows the saved copies of the reads this page made (tracked below), read-only.
import { fmtDateTimeFirm } from "@/lib/dt";

export const FALLBACK_EVENT = "azex-saved-copy";
export const NO_COPY_CODE = "SCREEN_NO_SAVED_COPY";

export const SAVED_COPY_TEXT = {
  en: {
    banner: "Showing the saved version of {when} by {who} – live view unavailable",
    readOnly: "Editing is off in this view.",
    neverSaved: "This screen could not be opened and no saved version of it exists yet.",
    renderFailed: "This screen could not be shown. The saved version is shown below, read-only.",
    loading: "Loading the saved version…",
    someone: "a team member",
    writeRefused: "Not saved — this is the saved version (read-only) while the live view is unavailable.",
  },
  ar: {
    banner: "يُعرض الإصدار المحفوظ بتاريخ {when} بواسطة {who} – العرض المباشر غير متاح",
    readOnly: "التعديل متوقف في هذا العرض.",
    neverSaved: "تعذّر فتح هذه الشاشة ولا يوجد لها إصدار محفوظ بعد.",
    renderFailed: "تعذّر عرض هذه الشاشة. يُعرض الإصدار المحفوظ أدناه للقراءة فقط.",
    loading: "جارٍ تحميل الإصدار المحفوظ…",
    someone: "أحد أعضاء الفريق",
    writeRefused: "لم يُحفظ — هذا هو الإصدار المحفوظ (للقراءة فقط) ما دام العرض المباشر غير متاح.",
  },
};

export function bannerText(lang, savedAt, savedBy) {
  const L = SAVED_COPY_TEXT[lang === "ar" ? "ar" : "en"];
  return L.banner.replace("{when}", savedAt ? fmtDateTimeFirm(savedAt) : "—").replace("{who}", savedBy || L.someone);
}

// ---- the API reads made on the current page (so a page that fails to render can ask for their saved copies)
let reads = [];
let readsPage = "";
export function noteRead(url) {
  try {
    const page = window.location.pathname;
    if (page !== readsPage) { reads = []; readsPage = page; }
    const u = String(url || "");
    if (u && !reads.includes(u)) reads = [...reads, u].slice(-40);
  } catch { /* never block a response */ }
}
export function pageReads() {
  try { return window.location.pathname === readsPage ? reads.slice() : []; } catch { return []; }
}

// ---- Batch 31 · item 17 (M85): while a screen shows its saved version, editing is off — navigation is not.
// The frame (SavedCopyFrame) turns the read-only mode on; lib/api.js then refuses every write the screen tries (client-side,
// nothing reaches the server) with the message below. Reads, exports / downloads and the app's own housekeeping calls go on.
export const READ_ONLY_CODE = "SAVED_COPY_READ_ONLY";
let readOnly = false;
export function setReadOnly(on) { readOnly = !!on; }
export function isReadOnly() { return readOnly; }
// writes that never change saved work: error reports, sign-in / session, presence, download links, exports / previews
const WRITE_ALLOWED = /(\/client-errors|\/auth\/|\/presence|\/download-token|\/last-good|export|download|\.pdf|\.xlsx|\.docx|\.csv|\/preview)/i;
/** The refusal for a write made while read-only (an Error carrying READ_ONLY_CODE), else null. */
export function refusedWrite(cfg, lang) {
  if (!readOnly) return null;
  const m = String((cfg && cfg.method) || "get").toLowerCase();
  if (m === "get" || m === "head" || m === "options") return null;
  if (WRITE_ALLOWED.test(String((cfg && cfg.url) || ""))) return null;
  const L = SAVED_COPY_TEXT[lang === "ar" ? "ar" : "en"];
  const err = new Error(L.writeRefused);
  err.code = READ_ONLY_CODE;
  err._savedCopyRefused = true;
  err.config = cfg;
  err.response = { status: 423, data: { detail: L.writeRefused, detail_ar: SAVED_COPY_TEXT.ar.writeRefused,
                                        error_code: READ_ONLY_CODE } };
  return err;
}

// controls that change saved work — turned off in the saved-version view; anything else (tabs, folders, open, expand,
// export, print, links) stays usable
const EDIT_ACTION = /\b(save|saved|sign|signoff|sign-off|send|upload|delete|remove|submit|approve|reject|post|reverse|import|replace|restore|attach|add|create|mark|complete|lock|unlock|recompute|rescan|rerun|re-run|run|generate|edit|clear|reset|apply)\b/i;
const NAV_ACTION = /\b(export|download|pdf|print|open|view|expand|collapse|show|hide|next|previous|prev|back|close|cancel|tab|filter|search)\b/i;
export function isEditControl(el) {
  try {
    const label = [el.getAttribute("data-testid"), el.getAttribute("aria-label"), el.getAttribute("title"), el.textContent]
      .filter(Boolean).join(" ").replace(/[-_]/g, " ");
    return EDIT_ACTION.test(label) && !NAV_ACTION.test(label);
  } catch { return false; }
}

// PR 45 review L9: search / filter fields only narrow what is shown — they stay usable in the saved-version view
const FIND_FIELD = /\b(search|filter|find|query)\b/i;
export function isFindField(el) {
  try {
    if ((el.getAttribute("type") || "").toLowerCase() === "search" || el.getAttribute("role") === "searchbox") return true;
    const label = [el.getAttribute("data-testid"), el.getAttribute("aria-label"), el.getAttribute("placeholder"),
                   el.getAttribute("name"), el.getAttribute("title")].filter(Boolean).join(" ").replace(/[-_]/g, " ");
    return FIND_FIELD.test(label);
  } catch { return false; }
}

const MARK = "data-saved-copy-ro";
/** A sign-in / step-up dialog (it asks for a password) or anything marked ``data-saved-copy-exempt`` stays usable. */
export function isExempt(el) {
  try {
    if (el.closest("[data-saved-copy-exempt]")) return true;
    const d = el.closest("[role='dialog']");
    return !!(d && d.querySelector("input[type='password']"));
  } catch { return false; }
}
/** Turn editing off inside ``root``: text fields read-only, choices / files / edit buttons disabled. Returns an undo. */
export function lockEditing(root) {
  if (!root) return () => {};
  root.querySelectorAll("input, textarea, select, button, [contenteditable='true']").forEach((el) => {
    if (el.hasAttribute(MARK) || isExempt(el)) return;
    const tag = el.tagName.toLowerCase();
    if ((tag === "input" || tag === "select") && isFindField(el)) return;
    const type = (el.getAttribute("type") || "").toLowerCase();
    if ((tag === "input" && !["checkbox", "radio", "file", "button", "submit", "range", "color"].includes(type)) || tag === "textarea") {
      if (el.readOnly) return;
      el.readOnly = true; el.setAttribute("aria-readonly", "true"); el.setAttribute(MARK, "readonly");
    } else if (tag === "select" || tag === "input") {
      if (el.disabled) return;
      el.disabled = true; el.setAttribute(MARK, "disabled");
    } else if (tag === "button") {
      if (el.disabled || !isEditControl(el)) return;
      el.disabled = true; el.setAttribute(MARK, "disabled");
    } else {
      el.setAttribute("contenteditable", "false"); el.setAttribute(MARK, "contenteditable");
    }
  });
  return () => unlockEditing(root);
}
export function unlockEditing(root) {
  if (!root) return;
  root.querySelectorAll(`[${MARK}]`).forEach((el) => {
    const how = el.getAttribute(MARK);
    if (how === "readonly") { el.readOnly = false; el.removeAttribute("aria-readonly"); }
    else if (how === "disabled") el.disabled = false;
    else if (how === "contenteditable") el.setAttribute("contenteditable", "true");
    el.removeAttribute(MARK);
  });
}

export function announce(detail) {
  try { window.dispatchEvent(new CustomEvent(FALLBACK_EVENT, { detail })); } catch { /* no window */ }
}

/** The API path (+ query) of an axios config, as the server keys the copies: "/api/…?a=1". */
export function apiPathOf(cfg) {
  try {
    const raw = String((cfg && cfg.url) || "");
    const u = new URL(raw, "http://x/api/");
    let path = u.pathname;
    if (!path.startsWith("/api/")) path = "/api" + (path.startsWith("/") ? "" : "/") + path;
    const params = new URLSearchParams(u.search);
    const extra = (cfg && cfg.params) || {};
    Object.keys(extra).forEach((k) => { if (extra[k] !== undefined && extra[k] !== null) params.set(k, String(extra[k])); });
    const qs = params.toString();
    return path + (qs ? "?" + qs : "");
  } catch { return ""; }
}
